import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

export type FormSectionStatus = {
  /** set to true - display spinning loader icon */
  isPending: boolean;
  /** set to true - displays error icon */
  isError?: boolean;
  /** set to true - displays success icon momentarily then display current time */
  isSuccess?: boolean;
  /** defaults to false - displays the retry button */
  showRetryButton?: boolean;
  /** retry function */
  retryFn?: () => void;
};

const SpinningLoopIcon = () => (
  <LoopOutlinedIcon
    fontSize="small"
    sx={{
      animation: 'spin 2s linear infinite',
      '@keyframes spin': {
        '0%': {
          transform: 'rotate(360deg)',
        },
        '100%': {
          transform: 'rotate(0deg)',
        },
      },
    }}
  />
);

const ErrorTooltipIcon = () => (
  <Tooltip title="Something went wrong!">
    <ErrorOutlineIcon color="error" />
  </Tooltip>
);

function FormSectionStatusIcons({
  isPending,
  isError,
  isSuccess,
  showRetryButton = false,
  retryFn,
}: FormSectionStatus) {
  const [showTimestamp, setShowTimestamp] = useState(false);

  useEffect(() => {
    if (isSuccess && !isPending) {
      const timer = setTimeout(() => setShowTimestamp(true), 3000);
      return () => clearTimeout(timer);
    }
    if (isPending) {
      setShowTimestamp(false);
    }
  }, [isSuccess, isPending]);

  const timestampVisible = isSuccess && !isPending && showTimestamp;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="right"
      sx={{
        color: 'grey.700',
      }}
    >
      {isPending && <SpinningLoopIcon />}
      {!isPending && isError && (
        <Stack direction="row" alignItems="center">
          <ErrorTooltipIcon />
          {showRetryButton && (
            <Button
              aria-label="retry"
              color="primary"
              onClick={retryFn}
              variant="contained"
              sx={{ marginLeft: 2, minWidth: (t) => t.spacing(18.5) }}
              size="small"
            >
              Retry
            </Button>
          )}
        </Stack>
      )}
      {timestampVisible && <Typography variant="caption">Last updated {DateTime.now().toFormat('h:mm a')}</Typography>}
      {!timestampVisible && isSuccess && (
        <Stack direction="row" spacing={1} alignItems="center">
          <DoneOutlinedIcon color="success" />
          <Typography variant="caption" color="success.main">
            Saved
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

export default FormSectionStatusIcons;
